import React, {
  ComponentType,
  useCallback,
  useId,
  useMemo,
  useState,
} from "react";

import type { Dayjs } from "dayjs";
import type { ChartOptions } from "chart.js";
import { ChevronDown, ChevronUp, Filter } from "react-feather";

import DatePickerJs from "../../../shared/DatePicker/DatePicker";
import ExportToXlsxButtonJs from "../../../CommonComponents/ExportToXlsxButton/ExportToXlsxButton";
import Button from "../../../shared/Button/Button";
import Select from "../../../shared/Select/Select";
import {
  formatDateNoTimezone,
  getAdminNow,
} from "../../../../utils/dateService";
import {
  formatCompactCountTick,
  formatCompactCurrencyTick,
  formatCurrency,
} from "../../../../utils/financialFormatters";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { DATE_RANGE } from "../../../../utils/Constants";
import {
  defaultDateRangeForViewport,
  defaultStartRangeForViewport,
} from "../../../../utils/narrowViewportChartDates";
import { BarChart, ChartCard, CHART_PALETTE } from "../../../shared/Charts";
import { type CasinoDailyPerformancePoint } from "../casinoKpisV2MockData";
import {
  type CasinoDailyFilterOption,
  type CasinoDailyFilterOptions,
  useGetCasinoDailyPerformance,
} from "../../../../queries/casino-kpis/useGetCasinoDailyPerformance";
import { useCurrencySymbol } from "../../../../hooks/useCurrencySymbol";
import type { CasinoKpiFilterOption } from "../../../../queries/casino-kpis/casinoKpiFilters";

import styles from "./CasinoDailyPerformance.module.css";

const CustomDatePicker = DatePickerJs as ComponentType<any>;
const ExportToXlsxButton = ExportToXlsxButtonJs as ComponentType<any>;

type DateRange = [Dayjs, Dayjs];

const SERIES = [
  { key: "rounds" as const, color: "#e8c547", axis: "y1" as const },
  { key: "turnover" as const, color: CHART_PALETTE.seriesGreen, axis: "y" as const },
  { key: "ggr" as const, color: CHART_PALETTE.seriesPurple, axis: "y" as const },
];

const SERIES_LABELS: Record<(typeof SERIES)[number]["key"], string> = {
  turnover: AppStrings.TURNOVER,
  ggr: AppStrings.GGR,
  rounds: AppStrings.ROUNDS,
};

const ROUNDS_DATASET_INDEX = SERIES.findIndex((s) => s.key === "rounds");

// `DatePicker.getData` is required but the chart re-fetches reactively via
// `dateFilters` -> queryKey. Hoisting the no-op to module scope makes the
// intent obvious and avoids a needless callback per render.
const NOOP_GET_DATA = (_queryString: string | null): void => undefined;

// Catch-all dropdown defaults shown until the BE filter catalog arrives.
// Production reads `data?.filterOptions` from the daily endpoint instead;
// these are only the first-render placeholders + tests.
const DEFAULT_PROVIDER_OPTIONS: CasinoDailyFilterOption[] = [
  { value: "all", label: AppStrings.ALL_PROVIDERS },
];
const DEFAULT_GAME_OPTIONS: CasinoDailyFilterOption[] = [
  { value: "all", label: AppStrings.ALL_GAMES },
];

const ALL_VALUE = "all";

export type CasinoDailyPerformanceProps = {
  /**
   * Optional override for the chart points. Used by tests / Storybook to
   * render a deterministic dataset; production wires the live API hook.
   */
  points?: CasinoDailyPerformancePoint[];
  providerOptions?: CasinoDailyFilterOption[];
  gameOptions?: CasinoDailyFilterOption[];
  moneyTypeOptions?: CasinoKpiFilterOption[];
};

const CasinoDailyPerformance: React.FC<CasinoDailyPerformanceProps> = ({
  points: pointsProp,
  providerOptions: providerOptionsProp,
  gameOptions: gameOptionsProp,
  moneyTypeOptions = [],
}) => {
  // Last 30 days on desktop (today - 29 -> today). At 768px and below the shared
  // viewport helper swaps in last 7 days so the chart stays readable.
  const [dates, setDates] = useState<DateRange>(() =>
    defaultDateRangeForViewport(() => {
      const now = getAdminNow();
      return [now.subtract(29, "day").startOf("day"), now.endOf("day")];
    }),
  );
  const [startRange] = useState(() =>
    defaultStartRangeForViewport(DATE_RANGE.LAST_30_DAYS),
  );
  const [provider, setProvider] = useState(ALL_VALUE);
  const [game, setGame] = useState(ALL_VALUE);
  const [moneyTypes, setMoneyTypes] = useState<string[]>([]);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const filtersId = useId();

  const currencySymbol = useCurrencySymbol() ?? "$";

  // Date / filter changes drive the query key so the hook refetches itself.
  // Defensive `?.format() ?? ""` guards against a future change to the date
  // picker (e.g. enabling `allowClearToNull`) handing us a missing endpoint
  // — the hook treats empty strings as "disabled" and skips the request.
  // Provider / game use the `[eq]` bracket keys the BE expects for equality
  // filters; the hook forwards them verbatim as querystring parameters.
  const queryFilters = useMemo(
    () => ({
      start_date: dates[0]?.format("YYYY-MM-DD") ?? "",
      end_date: dates[1]?.format("YYYY-MM-DD") ?? "",
      "provider[eq]": provider,
      "game[eq]": game,
      money_type: moneyTypes,
    }),
    [dates, provider, game, moneyTypes],
  );

  const { data, isFetching, isLoading, isError } =
    useGetCasinoDailyPerformance(queryFilters);

  const showLoading = isLoading || isFetching;

  // The `points` prop wins when supplied (tests / storybook); otherwise
  // we render the live API points. Memoised so the empty-array fallback
  // doesn't allocate a new reference on every render and bust the
  // downstream `useMemo` deps for chart data + tooltips.
  const points = useMemo(
    () => pointsProp ?? data?.points ?? [],
    [pointsProp, data?.points],
  );

  // Provider catalog comes from the BE; until the first response lands we
  // show just the "All Providers" placeholder so the dropdown is always
  // operable. Tests can pass `providerOptions` to bypass the live call.
  //
  // Games are resolved client-side from `filter.game.cascading_options` —
  // the BE ships the full provider -> games map in every response, so
  // switching providers doesn't need to wait for a refetch to update the
  // dropdown. When `provider === "all"` we surface the flat union of
  // every provider's games (deduped, alphabetically sorted) because the
  // endpoint accepts `game[eq]=<name>` on its own; the admin can narrow
  // by game without picking a provider first.
  //
  // The removed 2026-07-17 cascade regression came from *caching* a
  // narrowed list across responses; a stateless per-render lookup into
  // the always-full map is safe.
  const liveFilterOptions: CasinoDailyFilterOptions | undefined = data?.filterOptions;
  const providerOptions = useMemo<CasinoDailyFilterOption[]>(
    () =>
      providerOptionsProp ??
      liveFilterOptions?.providers ??
      DEFAULT_PROVIDER_OPTIONS,
    [providerOptionsProp, liveFilterOptions?.providers],
  );
  const gameOptions = useMemo<CasinoDailyFilterOption[]>(() => {
    if (gameOptionsProp) return gameOptionsProp;
    const map = liveFilterOptions?.gamesByProvider;
    if (provider !== ALL_VALUE) {
      return map?.[provider] ?? DEFAULT_GAME_OPTIONS;
    }
    if (!map) return DEFAULT_GAME_OPTIONS;
    // Flatten every provider's games into a single deduped, alpha-sorted
    // list so the Game dropdown is meaningful when no provider is
    // selected. The same game name can legitimately appear in multiple
    // providers (e.g. "Blackjack" ships from evolution, iconic21,
    // jacktop...) — the querystring just carries the name, so a single
    // entry is enough.
    const seen = new Set<string>();
    const flat: CasinoDailyFilterOption[] = [];
    for (const bucket of Object.values(map)) {
      for (const opt of bucket) {
        if (opt.value === ALL_VALUE || seen.has(opt.value)) continue;
        seen.add(opt.value);
        flat.push(opt);
      }
    }
    flat.sort((a, b) => a.label.localeCompare(b.label));
    return [DEFAULT_GAME_OPTIONS[0], ...flat];
  }, [gameOptionsProp, liveFilterOptions?.gamesByProvider, provider]);

  // Reverse index of the cascade map: game value -> providers[] that ship
  // it. Powers the "auto-select provider on unambiguous game pick" behavior
  // in `handleGameChange` below without walking the whole cascade on every
  // interaction. Rebuilt only when the underlying map changes; cheap
  // enough even for the ~1k-game catalog the BE ships in production.
  const gameToProviders = useMemo(() => {
    const map = liveFilterOptions?.gamesByProvider;
    const index = new Map<string, string[]>();
    if (!map) return index;
    for (const [providerKey, bucket] of Object.entries(map)) {
      for (const opt of bucket) {
        if (opt.value === ALL_VALUE) continue;
        const existing = index.get(opt.value);
        if (existing) {
          existing.push(providerKey);
        } else {
          index.set(opt.value, [providerKey]);
        }
      }
    }
    return index;
  }, [liveFilterOptions?.gamesByProvider]);

  // Reset game synchronously on provider change so React Query only fires
  // a single request with the correct {provider, game} pair. Doing this in
  // an effect would cause two re-renders: the first with the now-stale
  // game (e.g. provider=evolution, game=Blackjack — a bgaming game), which
  // would dispatch a contradictory request to ClickHouse before the
  // effect-driven reset triggered the corrected refetch. We keep this
  // even now that game is a standalone filter — the alternative ("keep
  // game if it exists in the new provider's cascade") is the exact
  // client-side cleverness the 2026-07-17 comment warned against.
  const handleProviderChange = useCallback((next: string) => {
    setProvider(next);
    setGame(ALL_VALUE);
  }, []);

  // Mirror of `handleProviderChange` in the opposite direction: when the
  // admin picks a game that ships under exactly one provider, we auto-
  // narrow the Provider dropdown to that provider so the pair on the
  // wire matches the admin's intent (and the header reflects the actual
  // scope of the chart). Games shared across multiple providers are
  // intentionally left alone — the admin is looking at cross-provider
  // data for that title, so forcing an arbitrary pick would be wrong.
  const handleGameChange = useCallback(
    (next: string) => {
      setGame(next);
      if (next === ALL_VALUE) return;
      const providers = gameToProviders.get(next);
      if (providers && providers.length === 1) {
        setProvider(providers[0]);
      }
    },
    [gameToProviders],
  );

  const chartData = useMemo(
    () => ({
      labels: points.map((p) => formatDateNoTimezone(p.date, "MMM D")),
      datasets: SERIES.map((s) => ({
        label: SERIES_LABELS[s.key],
        data: points.map((p) => p[s.key]),
        backgroundColor: s.color,
        borderRadius: 3,
        yAxisID: s.axis,
        barPercentage: 1.0,
        categoryPercentage: 0.7,
      })),
    }),
    [points],
  );

  const chartOptions = useMemo<ChartOptions<"bar">>(() => {
    const yValues = points.flatMap((p) => [p.turnover, p.ggr]);
    const y1Values = points.map((p) => p.rounds);

    const HEADROOM = 1.05;
    const yMaxPadded = Math.max(0, ...yValues) * HEADROOM;
    const yMinPadded = Math.min(0, ...yValues) * HEADROOM;
    const y1MaxPadded = Math.max(0, ...y1Values) * HEADROOM;
    const y1MinPadded = Math.min(0, ...y1Values) * HEADROOM;

    const yNegRatio = yMaxPadded > 0 ? -yMinPadded / yMaxPadded : 0;
    const y1NegRatio = y1MaxPadded > 0 ? -y1MinPadded / y1MaxPadded : 0;
    const negRatio = Math.max(yNegRatio, y1NegRatio);

    const yScale =
      negRatio > 0
        ? { min: -negRatio * yMaxPadded, max: yMaxPadded }
        : { beginAtZero: true as const };
    const y1Scale =
      negRatio > 0
        ? { min: -negRatio * y1MaxPadded, max: y1MaxPadded }
        : { beginAtZero: true as const };

    return {
      datasets: {
        bar: {
          barPercentage: 1.0,
          categoryPercentage: 0.7,
        },
      },
      interaction: { mode: "index", intersect: false },
      scales: {
        y: {
          type: "linear",
          position: "left",
          ...yScale,
          title: {
            display: true,
            text: `${AppStrings.TURNOVER} / ${AppStrings.GGR}`,
          },
          ticks: {
            callback: (value) =>
              formatCompactCurrencyTick(Number(value), currencySymbol),
          },
        },
        y1: {
          type: "linear",
          position: "right",
          ...y1Scale,
          title: {
            display: true,
            text: AppStrings.ROUNDS,
          },
          grid: { drawOnChartArea: false },
          ticks: {
            callback: (value) => formatCompactCountTick(Number(value)),
          },
        },
      },
      plugins: {
        tooltip: {
          mode: "index",
          intersect: false,
          displayColors: true,
          callbacks: {
            title: (items) => {
              if (!items?.length) return "";
              const iso = points[items[0].dataIndex]?.date;
              return iso ? formatDateNoTimezone(iso, "MMM D, YYYY") : "";
            },
            // Append the series label after the value so the tooltip reads
            // "$106 Turnover" / "$44 GGR" / "209 Rounds" instead of bare
            // numbers admins have to map to colour swatches.
            label: (item) => {
              const raw = Number(item.raw);
              if (item.datasetIndex === ROUNDS_DATASET_INDEX) {
                return `${raw.toLocaleString("en-US")} ${AppStrings.ROUNDS}`;
              }
              const series = SERIES[item.datasetIndex];
              const value = formatCurrency(raw, currencySymbol);
              return series ? `${value} ${SERIES_LABELS[series.key]}` : value;
            },
          },
        },
      },
    };
  }, [points, currencySymbol]);

  const exportRows = useMemo(
    () =>
      points.map((p) => ({
        date: p.date,
        provider,
        game,
        turnover: p.turnover,
        ggr: p.ggr,
        rounds: p.rounds,
      })),
    [points, provider, game],
  );

  const headerContent = (
    <div className={styles.headerLeft}>
      <div className={styles.titleRow}>
        <h2 className={styles.title}>{AppStrings.DAILY_PERFORMANCE}</h2>
        <span className={styles.filterToggle}>
          <Button
            onClick={() => setFiltersVisible((visible) => !visible)}
            aria-expanded={filtersVisible}
            aria-controls={filtersId}
            aria-label={AppStrings.PAM_REPORT_FILTERS}
          >
            <Filter size={18} aria-hidden="true" />
            {filtersVisible ? (
              <ChevronUp size={16} aria-hidden="true" />
            ) : (
              <ChevronDown size={16} aria-hidden="true" />
            )}
          </Button>
        </span>
        <div
          id={filtersId}
          className={`${styles.filters}${
            filtersVisible ? "" : ` ${styles.filtersCollapsed}`
          }`}
          data-testid="casino-daily-filters"
        >
          <Select
            className={styles.filterSelect}
            value={provider}
            options={providerOptions}
            onChange={handleProviderChange}
            aria-label={AppStrings.PROVIDER}
            showSearch
            optionFilterProp="label"
            // Inline spinner while the request is in flight — the admin can
            // still open the dropdown and change the value; this just
            // surfaces "the chart you're staring at is being refreshed".
            loading={showLoading}
          />
          <Select
            className={styles.filterSelect}
            value={game}
            options={gameOptions}
            onChange={handleGameChange}
            aria-label={AppStrings.GAME}
            showSearch
            optionFilterProp="label"
            loading={showLoading}
          />
          <Select
            className={styles.filterSelect}
            mode="multiple"
            value={moneyTypes}
            options={moneyTypeOptions}
            onChange={(value) => setMoneyTypes(value as string[])}
            aria-label={AppStrings.CASINO_KPI_CURRENCY_TYPE}
            placeholder={AppStrings.CASINO_KPI_ALL_CURRENCY_TYPES}
            maxTagCount="responsive"
            showSearch
            optionFilterProp="label"
            loading={showLoading}
          />
        </div>
      </div>
      <ul className={styles.legend}>
        {SERIES.map((s) => (
          <li key={s.key} className={styles.legendItem}>
            <span
              className={styles.legendDot}
              style={{ backgroundColor: s.color }}
              aria-hidden="true"
            />
            {SERIES_LABELS[s.key]}
          </li>
        ))}
      </ul>
    </div>
  );

  // The hook's dense-fill emits a zero row for every day in the range, so
  // `points.length === 0` only happens when the request literally returns
  // nothing (degenerate / invalid range, or the prop override is empty).
  const hasNonZeroPoint = points.some(
    (p) => p.turnover > 0 || p.ggr > 0 || p.rounds > 0,
  );
  const showEmpty =
    !showLoading && !isError && pointsProp === undefined && !hasNonZeroPoint;

  return (
    <ChartCard
      ariaLabel={AppStrings.DAILY_PERFORMANCE}
      headerLayout="row"
      headerExtra={headerContent}
      bodyClassName={styles.chartContainer}
      controls={
        <>
          <CustomDatePicker
            getData={NOOP_GET_DATA}
            dates={dates}
            setDates={setDates}
            showPresets={true}
            startRange={startRange}
          />
          <ExportToXlsxButton
            data={exportRows}
            filename="casino-daily-performance.xlsx"
          />
        </>
      }
    >
      {isError ? (
        <p
          className={styles.errorBanner}
          role="alert"
          data-testid="casino-daily-performance-error"
        >
          {AppStrings.CASINO_DAILY_LOAD_ERROR}
        </p>
      ) : showEmpty ? (
        <p
          className={styles.emptyMessage}
          data-testid="casino-daily-performance-empty"
        >
          {AppStrings.PAM_CHART_EMPTY_STATE}
        </p>
      ) : (
        <BarChart
          data={chartData}
          optionsOverride={chartOptions}
          isLoading={showLoading && pointsProp === undefined}
        />
      )}
    </ChartCard>
  );
};

export default CasinoDailyPerformance;
